import React, { useEffect, useState, useMemo } from 'react';
import getConfig from 'next/config';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { Form } from 'react-bootstrap';
import { components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';

import { config } from '_config';
import { usePrevious } from '../../hooks';
import { USPTOCategoryService } from '../../services/USPTOCategoryService';

const { publicRuntimeConfig } = getConfig();

const validateCategories = (classes, value) => {
  const categories = classes?.map((item) => {
    return item.goodsCategory;
  });

  if (categories?.filter((category) => category === value).length > 1) {
    return false;
  }

  return true;
};

export default function ClassNumberAsync({
  controlId,
  classes,
  goods,
  goodsCategory,
  descriptions,
  goodsServiceDetail,
  validated,
  onChange,
}) {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [hiddenInputValue, setHiddenInputValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const prevGoods = usePrevious(goods);
  const [clazzValue, setClazzValue] = useState(null);

  const handleChange = (option) => {
    setSearchInputValue('');
    setHiddenInputValue(option?.value);
    setClazzValue(option?.clazz);
    onChange &&
      onChange({
        target: {
          id: controlId + '_goodsCategory',
          value: option?.value,
          clazz: option?.clazz,
        },
      });
  };

  const handleSearchChange = (value) => {
    setSearchInputValue((prevValue) => {
      if ((prevValue?.length === 1 && !value) || value) {
        return value;
      }

      return prevValue;
    });
  };

  const loadCategoryOptions = async (search, prevOptions, { page }) => {
    const options = [];
    let hasMore = false;

    setIsLoading(true);

    try {
      const { elements, maxPerPage, totalResults } =
        await USPTOCategoryService.getList({
          page,
          count: 10,
          search,
          isGoods: goods === 'true',
        });

      hasMore = totalResults > maxPerPage * page;

      elements.forEach(({ termId, description, clazz }) => {
        options.push({
          label: `${termId} - ${description}`,
          value: `${termId} - ${description}`,
          clazz: clazz,
        });
      });
    } catch (e) {
      setIsLoading(false);

      return {
        options: [],
        hasMore: false,
        additional: {
          page: page,
        },
      };
    }

    setIsLoading(false);

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const isCategoryValid = useMemo(() => {
    return validateCategories(classes, hiddenInputValue);
  }, [classes, hiddenInputValue]);

  const classNamePrefix = clsx({
    ['async-select']: !validated,
    ['async-select-valid']: validated && hiddenInputValue && isCategoryValid,
    ['async-select-invalid']:
      (validated && !hiddenInputValue) || (validated && !isCategoryValid),
  });

  const LoadingIndicator = (props) => (
    <components.LoadingIndicator {...props} />
  );

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {!validated && (
          <img
            src={`${publicRuntimeConfig.basePath}/dropdown-arrow.svg`}
            alt=""
          />
        )}
        {validated && hiddenInputValue && isCategoryValid && (
          <img
            src={`${publicRuntimeConfig.basePath}/dropdown-arrow-success.svg`}
            alt=""
          />
        )}
        {((validated && !hiddenInputValue) ||
          (validated && !isCategoryValid)) && (
          <img
            src={`${publicRuntimeConfig.basePath}/dropdown-arrow-failure.svg`}
            alt=""
          />
        )}
      </components.DropdownIndicator>
    );
  };

  useEffect(() => {
    if (prevGoods !== goods && goodsCategory) {
      const checkGoodsCategory = async () => {
        setIsLoading(true);

        const { elements } = await USPTOCategoryService.getList({
          page: 1,
          count: 5,
          termId: goodsCategory?.split(' - ')[0],
          isGoods: goods,
        });

        const { termId, description } = elements[0] || {};
        setHiddenInputValue(termId ? `${termId} - ${description}` : null);
        setIsLoading(false);
      };

      checkGoodsCategory();
    }
  }, [prevGoods, goods, goodsCategory]);

  const isValid = hiddenInputValue && !isLoading && isCategoryValid;
  const isInvalid = !hiddenInputValue || isLoading || !isCategoryValid;
  return (
    <>
      <Typography>
        {goods === 'true' ? 'GOODS' : 'SERVICES'} - Which of these categories
        best describes the industry or way in which you will use your mark?
      </Typography>
      <Form.Group controlId={controlId + '_goodsCategory'} className="mb-4">
        <Form.Text className="text-muted">
          A specialist will reach out to you if they find that there is a better
          suiting category.
        </Form.Text>
        <Form.Control
          required
          readOnly
          hidden
          isValid={isValid}
          isInvalid={isInvalid}
          data-hj-allow
        />
        <AsyncPaginate
          key={goods}
          className="async-select-container"
          classNamePrefix={classNamePrefix}
          placeholder="Start typing to search categories…"
          components={{ DropdownIndicator, LoadingIndicator }}
          isLoading={isLoading}
          isClearable={!!hiddenInputValue}
          additional={{ page: 1 }}
          loadOptions={loadCategoryOptions}
          inputValue={searchInputValue}
          debounceTimeout={500}
          value={
            hiddenInputValue
              ? {
                  label: hiddenInputValue,
                  value: hiddenInputValue,
                  clazz: clazzValue,
                }
              : { label: 'Start typing to search categories…' }
          }
          onInputChange={handleSearchChange}
          onChange={handleChange}
        />
        {validated && isInvalid && (
          <Form.Control.Feedback type="invalid" style={{ paddingLeft: 15 }}>
            {!isCategoryValid
              ? 'Duplication of categories is not allowed'
              : 'Please, choose a category'}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      {config.skin !== 'v2-basic' &&
        goodsCategory?.match(/\{([^{}]*)\}/g)?.map((matches, index) => (
          <Form.Group
            key={controlId + 'descriptions' + index}
            className="mb-4"
            controlId={'descriptions' + index}
          >
            <Form.Label column>
              Please {matches.match(/\{([^{}]*)\}/)[1]}
            </Form.Label>
            <Form.Control
              required
              placeholder={'Please ' + matches.match(/\{([^{}]*)\}/)[1]}
              type="text"
              maxLength="30"
              data-hj-allow
              value={descriptions[index]}
              onChange={onChange}
              pattern="^(?!\s*$).+"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a description
            </Form.Control.Feedback>
          </Form.Group>
        ))}
      {config.skin === 'v2-basic' && (
        <>
          {goodsCategory?.match(/\{([^{}]*)\}/g)?.map((matches, index) => (
            <Form.Group
              key={controlId + 'descriptions' + index}
              className="mb-4"
              controlId={'descriptions' + index}
            >
              <Form.Label column>
                Please {matches.match(/\{([^{}]*)\}/)[1]}
              </Form.Label>
              <Form.Control
                required
                placeholder={'Please ' + matches.match(/\{([^{}]*)\}/)[1]}
                type="text"
                maxLength="30"
                data-hj-allow
                value={descriptions[index]}
                onChange={onChange}
                pattern="^(?!\s*$).+"
              />
              <Form.Control.Feedback type="invalid">
                Please provide a description
              </Form.Control.Feedback>
            </Form.Group>
          ))}
          {/* <Form.Group className="mb-4" controlId={'goodsServiceDetail'}>
            <Form.Label column>
              Please provide a brief description of the{' '}
              {goods === 'true' ? 'Goods' : 'Services'} you wish to trademark
            </Form.Label>
            <Form.Control
              as="textarea"
              minLength={5}
              type="text"
              maxLength="1000"
              style={{ resize: 'none', height: '160px' }}
              data-hj-allow
              value={goodsServiceDetail || ''}
              onChange={onChange}
              pattern="^(?!\s*$).+"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a description
            </Form.Control.Feedback>
          </Form.Group> */}
        </>
      )}
    </>
  );
}
